import React, { useState } from 'react';
import { Asset, Icon, Link, TAsset } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { TListingWarranty } from 'types/TListing';
import { trackGAEvent } from '../../../utils/gaTracking';

import Classes from './Warranty.module.scss';
import Price from './Price';

type TWarrantyProps = {
  warranty: TListingWarranty;
};

let DetailsModal;

const Warranty = ({ warranty }: TWarrantyProps) => {
  const { t } = useI18nContext();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isDetailsModalLoaded, setDetailsModalLoaded] = useState(false);

  const hasValidity = warranty.validity && warranty.validity > 0;
  const hasPrice = typeof warranty.price === 'number';
  const isPriceIncluded = hasPrice && warranty.price === 0;
  const hasDetails = !!warranty.details;
  const logoVertical = warranty.logo?.vertical;
  const logoHorizontal = warranty.logo?.horizontal;

  const title = t(`listing.trustIndicators.warranties.type.${warranty.type.toLowerCase()}`);

  const loadWarrantyModal = async () => {
    if (!isDetailsModalLoaded) {
      try {
        DetailsModal = (await import(/* webpackChunkName: "DetailsModal" */ './DetailsModal')).default;
        setDetailsModalLoaded(true);
      } catch {}
    }
  };

  const handleOpenModal = async () => {
    await loadWarrantyModal();
    setShowDetailsModal(true);
    trackGAEvent('WarrantyDetailsClicked', warranty.type);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
  };

  return (
    <div className={Classes.root}>
      {logoVertical ? (
        <span className={Classes.logoWrapper}>
          <Asset.Fluid name={logoVertical as TAsset} />
        </span>
      ) : (
        <span className={`${Classes.iconWrapper} ${isPriceIncluded ? Classes.iconWrapperIncluded : ''}`}>
          <Icon name="warranty" />
        </span>
      )}

      <div className={Classes.content}>
        <div>
          <p className={Classes.title}>{title}</p>
          <p>{warranty.name}</p>
          <Price price={warranty.price} />
          {hasValidity && (
            <p className={Classes.validity}>
              {t('listing.trustIndicators.warranties.validity', { validity: warranty.validity })}
            </p>
          )}
        </div>
        {hasDetails && <Link onClick={handleOpenModal}>{t('listing.trustIndicators.warranties.cta')}</Link>}
      </div>

      {hasDetails && showDetailsModal && isDetailsModalLoaded && (
        <DetailsModal
          title={title}
          content={warranty.description}
          disclaimer={warranty.disclaimer}
          handleClose={handleCloseModal}
          logo={logoHorizontal}
        />
      )}
    </div>
  );
};

export default Warranty;
